import { useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Dropdown,
  Option,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { Building24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import type { ReceptionDto } from '@api/generated/visitor-api/model';
import { useQuery } from '@tanstack/react-query';
import { visitorApiInstance } from '@api/generated/axios-instance';
import { useAuth } from '@app/providers/AuthProvider';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  dropdown: {
    minWidth: '200px',
  },
  icon: {
    color: tokens.colorBrandForeground1,
  },
});

interface ReceptionSelectorProps {
  selectedReceptionId?: string;
  onReceptionChange: (receptionId: string, reception?: ReceptionDto) => void;
  showIcon?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

export function ReceptionSelector({
  selectedReceptionId,
  onReceptionChange,
  showIcon = true,
  disabled = false,
  placeholder,
}: ReceptionSelectorProps) {
  const styles = useStyles();
  const { t } = useTranslation('common');
  const { user } = useAuth();

  // Get tenant ID from UserInfo (BFF provides tenantId directly)
  const tenantId = user?.tenantId;

  const { data: tenantReceptions, isLoading: isLoadingTenant, error: tenantError } = useQuery({
    queryKey: ['admin', 'tenants', tenantId, 'receptions'],
    queryFn: () => visitorApiInstance<ReceptionDto[]>({ url: `/api/v1/Tenant/${tenantId}/receptions`, method: 'GET' }),
    enabled: !!user && !!tenantId,
  });

  const { data: allReceptions, isLoading: isLoadingAll, error: allError } = useQuery({
    queryKey: ['admin', 'receptions'],
    queryFn: () => visitorApiInstance<ReceptionDto[]>({ url: '/api/v1/Reception', method: 'GET' }),
    enabled: !!user && !tenantId,
  });

  // Use tenant-filtered receptions if available, otherwise all receptions
  const receptions = tenantId ? tenantReceptions : allReceptions;
  const isLoading = tenantId ? isLoadingTenant : isLoadingAll;
  const error = tenantId ? tenantError : allError;

  // Auto-select first reception if none selected
  useEffect(() => {
    if (!selectedReceptionId && receptions && receptions.length > 0 && receptions[0].id) {
      onReceptionChange(receptions[0].id, receptions[0]);
    }
  }, [selectedReceptionId, receptions, onReceptionChange]);

  const handleChange = (_: unknown, data: { optionValue?: string }) => {
    if (data.optionValue) {
      const reception = receptions?.find((r) => r.id === data.optionValue);
      onReceptionChange(data.optionValue, reception);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <Spinner size="tiny" />
        <Text size={200}>{t('loading')}</Text>
      </div>
    );
  }

  if (error) {
    return (
      <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>
        {t('errorLoadingReceptions')}
      </Text>
    );
  }

  if (!receptions || receptions.length === 0) {
    return (
      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
        {t('noReceptionsAvailable')}
      </Text>
    );
  }

  return (
    <div className={styles.container}>
      {showIcon && <Building24Regular className={styles.icon} />}
      <Dropdown
        className={styles.dropdown}
        placeholder={placeholder || t('selectReception')}
        value={receptions.find((r) => r.id === selectedReceptionId)?.name || ''}
        selectedOptions={selectedReceptionId ? [selectedReceptionId] : []}
        onOptionSelect={handleChange}
        disabled={disabled}
      >
        {receptions.filter((r) => r.id).map((reception) => (
          <Option key={reception.id} value={reception.id!}>
            {reception.name ?? ''}
          </Option>
        ))}
      </Dropdown>
    </div>
  );
}

export default ReceptionSelector;
